import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Library,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  BookOpen,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useChaptersBySeriesId } from "../../hooks/useChapters";
import styles from "./BookPreview.module.css";

function BookPreview() {
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem("ebookFontSize") || "medium";
  });
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("ebookTheme") || "light";
  });

  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Get route parameters
  const { id, slug } = useParams();
  const location = useLocation();
  const seriesId = location.state?.seriesId || id;
  const navigate = useNavigate();

  // Get series data from hook
  const { series, chapters } = useChaptersBySeriesId(seriesId);

  // Helper function to format title for URL
  const formatTitleForUrl = useCallback((title) => {
    if (!title) return "";

    if (/[^\u0000-\u007F]/.test(title)) {
      return title;
    }

    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  }, []);

  // Define navigation functions
  const navigateBack = useCallback(() => {
    navigate(`/book-details/${chapters[0].ebook_slug}`);
  }, [navigate, chapters]);

  const calculatePages = useCallback(() => {
    if (!contentRef.current || !containerRef.current) return;

    const content = contentRef.current;
    const container = containerRef.current;

    // Get container dimensions
    const containerHeight = container.clientHeight;

    // Calculate how many pages we need
    const contentHeight = content.scrollHeight;
    const pagesCount = Math.ceil(contentHeight / containerHeight);

    setTotalPages(pagesCount > 0 ? pagesCount : 1);
  }, []);

  // Save current position to localStorage
  const savePosition = useCallback(() => {
    if (!chapters || chapters.length === 0) return;

    const bookId = `book_${seriesId}`;

    // Save overall book position
    const overallPosition = {
      chapterIndex: currentChapterIndex,
      page: currentPage,
    };
    localStorage.setItem(bookId, JSON.stringify(overallPosition));

    // Save chapter-specific position
    if (currentChapterIndex >= 0 && currentChapterIndex < chapters.length) {
      const chapterId = chapters[currentChapterIndex].id;
      const chapterPosition = {
        page: currentPage,
      };
      localStorage.setItem(
        `${bookId}_chapter_${chapterId}`,
        JSON.stringify(chapterPosition)
      );
    }
  }, [chapters, seriesId, currentChapterIndex, currentPage]);

  // Load saved position from localStorage
  const loadSavedPosition = useCallback(() => {
    if (!chapters || chapters.length === 0) return;

    const bookId = `book_${seriesId}`;
    const savedPosition = localStorage.getItem(bookId);

    if (savedPosition) {
      const position = JSON.parse(savedPosition);

      // If chapter index is valid
      if (
        position.chapterIndex >= 0 &&
        position.chapterIndex < chapters.length
      ) {
        // Set current chapter
        setCurrentChapterIndex(position.chapterIndex);

        // Navigate to the saved chapter if different from current
        const chapterTitle = chapters[position.chapterIndex].ebook_title;
        const formattedTitle = formatTitleForUrl(chapterTitle);

        if (slug !== formattedTitle) {
          navigate(`/book/${id}/episode/${formattedTitle}`, {
            state: { seriesId, fromSavedPosition: true },
          });
        }
      }
    }
  }, [chapters, seriesId, navigate, id, formatTitleForUrl, slug]);

  // Load chapter-specific page
  const loadChapterPage = useCallback(
    (chapterIndex) => {
      if (
        !chapters ||
        chapters.length === 0 ||
        chapterIndex < 0 ||
        chapterIndex >= chapters.length
      )
        return;

      const bookId = `book_${seriesId}`;
      const chapterId = chapters[chapterIndex].id;
      const savedChapterPosition = localStorage.getItem(
        `${bookId}_chapter_${chapterId}`
      );

      if (savedChapterPosition) {
        const position = JSON.parse(savedChapterPosition);
        if (position.page >= 0) {
          // Allow a small delay to ensure content is rendered before setting page
          setTimeout(() => {
            setCurrentPage(position.page);
          }, 50);
        } else {
          setCurrentPage(0);
        }
      } else {
        setCurrentPage(0);
      }
    },
    [chapters, seriesId]
  );

  // Next page handler
  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    } else {
      // If last page, go to next chapter
      nextChapter();
    }
  }, [currentPage, totalPages]);

  // Previous page handler
  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    } else {
      // If first page, go to previous chapter and its last page
      if (currentChapterIndex > 0) {
        prevChapter(true); // true indicates we want to go to the last page
      }
    }
  }, [currentPage, currentChapterIndex]);

  const nextChapter = useCallback(
    (goToFirstPage = true) => {
      if (!chapters || currentChapterIndex >= chapters.length - 1 || animating)
        return;

      setAnimating(true);

      const nextChapterTitle = chapters[currentChapterIndex + 1].ebook_title;
      const formattedTitle = formatTitleForUrl(nextChapterTitle);
      const nextIndex = currentChapterIndex + 1;

      // Navigate without delay
      navigate(`/book/${id}/episode/${formattedTitle}`, {
        state: { seriesId },
      });

      // Update index first
      setCurrentChapterIndex(nextIndex);

      // Reset to first page when changing chapters, or load saved page
      if (goToFirstPage) {
        loadChapterPage(nextIndex);
      }

      // Set animating to false after a short delay to allow for smoother transitions
      setTimeout(() => {
        setAnimating(false);
      }, 100);
    },
    [
      chapters,
      currentChapterIndex,
      animating,
      navigate,
      id,
      seriesId,
      formatTitleForUrl,
      loadChapterPage,
    ]
  );
  useEffect(() => {
    const adjustPadding = () => {
      if (contentRef.current) {
        contentRef.current.style.paddingBottom = window.innerWidth < 768 ? '100px' : '40px';
      }
    };
    
    window.addEventListener('resize', adjustPadding);
    adjustPadding(); // Initial adjustment
    
    return () => {
      window.removeEventListener('resize', adjustPadding);
    };
  }, []);

  const prevChapter = useCallback(
    (goToLastPage = false) => {
      if (!chapters || currentChapterIndex <= 0 || animating) return;

      setAnimating(true);

      const prevChapterTitle = chapters[currentChapterIndex - 1].ebook_title;
      const formattedTitle = formatTitleForUrl(prevChapterTitle);
      const prevIndex = currentChapterIndex - 1;

      // Navigate without delay
      navigate(`/book/${id}/episode/${formattedTitle}`, {
        state: { seriesId },
      });

      // Update index first
      setCurrentChapterIndex(prevIndex);

      if (goToLastPage) {
        // We'll set to last page after content loads
        setTimeout(() => {
          setCurrentPage(Math.max(0, totalPages - 1));
        }, 50);
      } else {
        // Load saved page for the chapter
        loadChapterPage(prevIndex);
      }

      // Set animating to false after a short delay
      setTimeout(() => {
        setAnimating(false);
      }, 100);
    },
    [
      chapters,
      currentChapterIndex,
      animating,
      navigate,
      id,
      seriesId,
      formatTitleForUrl,
      totalPages,
      loadChapterPage,
    ]
  );

  const selectChapter = useCallback(
    (index) => {
      if (!chapters || index === currentChapterIndex || animating) return;

      setAnimating(true);

      const selectedChapterTitle = chapters[index].ebook_title;
      const formattedTitle = formatTitleForUrl(selectedChapterTitle);

      // Navigate first, without delay
      navigate(`/book/${id}/episode/${formattedTitle}`, {
        state: { seriesId },
      });

      // Update index
      setCurrentChapterIndex(index);

      // Load chapter-specific page after a slight delay to ensure content is available
      loadChapterPage(index);

      if (window.innerWidth < 768) {
        setShowLibrary(false);
      }

      // Set animating to false after a short delay
      setTimeout(() => {
        setAnimating(false);
      }, 100);
    },
    [
      chapters,
      currentChapterIndex,
      animating,
      navigate,
      id,
      seriesId,
      formatTitleForUrl,
      loadChapterPage,
    ]
  );

  // Toggle library sidebar
  const toggleLibrary = useCallback(() => {
    setShowLibrary((prev) => !prev);
  }, []);

  // Change font size and save to localStorage
  const changeFontSize = useCallback((size) => {
    setFontSize(size);
    localStorage.setItem("ebookFontSize", size);
  }, []);

  // Change theme and save to localStorage
  const changeTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("ebookTheme", newTheme);
  }, []);

  // Find the current chapter index based on the URL parameter
  useEffect(() => {
    if (chapters && chapters.length > 0 && slug) {
      // First try direct match with the slug
      const slugIndex = chapters.findIndex(
        (chapter) => chapter.ebook_slug === slug
      );

      let foundIndex = -1;
      if (slugIndex !== -1) {
        foundIndex = slugIndex;
      } else {
        // If direct slug match fails, try matching by formatted title
        const titleIndex = chapters.findIndex((chapter) => {
          const formattedTitle = formatTitleForUrl(chapter.ebook_title);
          return formattedTitle === slug;
        });

        if (titleIndex !== -1) {
          foundIndex = titleIndex;
        } else {
          // Default to first chapter
          foundIndex = 0;
        }
      }

      if (foundIndex !== currentChapterIndex) {
        setCurrentChapterIndex(foundIndex);
        // Initial load, load chapter-specific position
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          loadChapterPage(foundIndex);
        }
      }
    }
  }, [chapters, slug, formatTitleForUrl, currentChapterIndex, loadChapterPage]);

  // Load saved position when component mounts
  useEffect(() => {
    if (isInitialLoad.current && chapters && chapters.length > 0) {
      const fromSavedPosition = location.state?.fromSavedPosition;
      if (!fromSavedPosition) {
        loadSavedPosition();
      }
    }
  }, [loadSavedPosition, chapters, location.state]);

  // Calculate pages when content changes or window resizes
  useEffect(() => {
    // Use a slight delay to ensure content has rendered properly
    const timer = setTimeout(() => {
      calculatePages();
    }, 100);

    const handleResize = () => {
      calculatePages();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [calculatePages, currentChapterIndex, fontSize, theme]);

  // Save position when chapter or page changes
  useEffect(() => {
    // Only save after initial load
    if (!isInitialLoad.current) {
      savePosition();
    }
  }, [currentChapterIndex, currentPage, savePosition]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        nextPage();
      } else if (e.key === "ArrowLeft") {
        prevPage();
      } else if (e.key === "Escape") {
        navigateBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [nextPage, prevPage, navigateBack]);

  // Format content to handle HTML
  const formatContent = (content) => {
    return { __html: content };
  };

  // Loading state
  if (!chapters || chapters.length === 0 || !series) {
    return <div className={styles.loading}>Loading book content...</div>;
  }

  // Make sure we have a valid currentChapterIndex
  const validIndex = Math.min(
    Math.max(0, currentChapterIndex),
    chapters.length - 1
  );
  const currentChapter = chapters[validIndex];

  if (!currentChapter) {
    return <div className={styles.loading}>Chapter not found</div>;
  }

  // Calculate the content style based on current page
  const contentStyle = {
    transform: `translateY(${-currentPage * 100}%)`,
    transition: "transform 0.3s ease",
  };

  // Apply theme class
  const themeClass =
    theme === "dark"
      ? styles.darkTheme
      : theme === "sepia"
      ? styles.sepiaTheme
      : styles.lightTheme;

  // Apply font size class
  const fontSizeClass =
    fontSize === "small"
      ? styles.fontSmall
      : fontSize === "large"
      ? styles.fontLarge
      : styles.fontMedium;

  return (
    <div className={`${styles.bookContainer} ${themeClass}`}>
      {/* Library sidebar */}
      <div
        className={`${styles.librarySidebar} ${
          showLibrary ? styles.active : ""
        } ${themeClass}`}
      >
        <div className={styles.libraryHeader}>
          <h2>Library</h2>
          <button className={styles.closeButton} onClick={toggleLibrary}>
            <X size={24} />
          </button>
        </div>
        <div className={styles.chapterList}>
          <h3>{series.title}</h3>
          <p className={styles.author}>by {currentChapter.ebook_author}</p>
          {chapters.map((chapter, index) => (
            <div
              key={chapter.id}
              className={`${styles.chapterItem} ${
                index === validIndex ? styles.active : ""
              }`}
              onClick={() => selectChapter(index)}
            >
              <span className={styles.chapterNumber}>
                Chapter {chapter.chapter_number}
              </span>
              <span className={styles.chapterTitle}>{chapter.ebook_title}</span>
            </div>
          ))}
        </div>
        <div className={styles.readerSettings}>
          <div className={styles.settingsSection}>
            <h4>Appearance</h4>
            <div className={styles.themeOptions}>
              <button
                className={`${styles.themeButton} ${
                  theme === "light" ? styles.activeTheme : ""
                }`}
                onClick={() => changeTheme("light")}
              >
                <div
                  className={styles.themeCircle}
                  style={{ backgroundColor: "#EBEBEB" }}
                ></div>
                <span>Light</span>
              </button>
              <button
                className={`${styles.themeButton} ${
                  theme === "sepia" ? styles.activeTheme : ""
                }`}
                onClick={() => changeTheme("sepia")}
              >
                <div
                  className={styles.themeCircle}
                  style={{ backgroundColor: "#f6f0e0" }}
                ></div>
                <span>Sepia</span>
              </button>
              <button
                className={`${styles.themeButton} ${
                  theme === "dark" ? styles.activeTheme : ""
                }`}
                onClick={() => changeTheme("dark")}
              >
                <div
                  className={styles.themeCircle}
                  style={{ backgroundColor: "#262626" }}
                ></div>
                <span>Dark</span>
              </button>
            </div>
          </div>
          <div className={styles.settingsSection}>
            <h4>Text Size</h4>
            <div className={styles.fontSizeOptions}>
              <button
                className={`${styles.fontSizeButton} ${
                  fontSize === "small" ? styles.activeSize : ""
                }`}
                onClick={() => changeFontSize("small")}
              >
                A<span className={styles.smallText}>A</span>
              </button>
              <button
                className={`${styles.fontSizeButton} ${
                  fontSize === "medium" ? styles.activeSize : ""
                }`}
                onClick={() => changeFontSize("medium")}
              >
                A<span className={styles.mediumText}>A</span>
              </button>
              <button
                className={`${styles.fontSizeButton} ${
                  fontSize === "large" ? styles.activeSize : ""
                }`}
                onClick={() => changeFontSize("large")}
              >
                A<span className={styles.largeText}>A</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main reading area */}
      <div className={`${styles.readerContainer} ${themeClass}`}>
        {/* Top navigation */}
        <div className={styles.readerHeader}>
          <button className={styles.libraryButton} onClick={toggleLibrary}>
            <Library size={24} />
          </button>
          <div className={styles.bookInfo}>
            <h2 className={styles.bookTitle}>{series.title}</h2>
            <p className={styles.chapterTitle}>{currentChapter.ebook_title}</p>
          </div>
          <div className={styles.pageIndicator}>
            <span>{currentPage + 1}</span>
            <span className={styles.pageTotal}>/{totalPages}</span>
          </div>
          <button className={styles.backButton} onClick={navigateBack}>
            <X size={24} />
          </button>
        </div>

        {/* Chapter content */}
        <div className={styles.contentContainer} ref={containerRef}>
          <div className={styles.pageWrapper}>
            <div
              className={`${styles.pageContent} ${fontSizeClass}`}
              style={contentStyle}
              ref={contentRef}
            >
              <div
                key={currentChapter.id}
                className={`${styles.chapterContent} ${
                  animating ? styles.fadeOut : styles.fadeIn
                }`}
                style={{
                  paddingBottom: window.innerWidth < 768 ? "100px" : "40px",
                }} // Add padding to prevent content being hidden by mobile nav
              >
                <h2 className={styles.contentChapterTitle}>
                  Chapter {currentChapter.chapter_number}:{" "}
                  {currentChapter.ebook_title}
                </h2>
                <div
                  className={styles.bookFullContent}
                  dangerouslySetInnerHTML={formatContent(
                    currentChapter.ebook_content
                  )}
                />
              </div>
            </div>
          </div>

          {/* Page navigation buttons */}
          <button
            className={`${styles.pageNavButton} ${styles.prevPageButton} ${
              currentPage === 0 && currentChapterIndex === 0
                ? styles.disabled
                : ""
            }`}
            onClick={prevPage}
            disabled={currentPage === 0 && currentChapterIndex === 0}
          >
            <ChevronLeft size={24} />
          </button>

          <button
            className={`${styles.pageNavButton} ${styles.nextPageButton} ${
              currentPage === totalPages - 1 &&
              currentChapterIndex === chapters.length - 1
                ? styles.disabled
                : ""
            }`}
            onClick={nextPage}
            disabled={
              currentPage === totalPages - 1 &&
              currentChapterIndex === chapters.length - 1
            }
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Bottom navigation */}
        <div className={styles.readerFooter}>
          <div className={styles.footerContent}>
            <span className={styles.footerChapter}>
              Chapter {currentChapter.chapter_number} of {chapters.length}
            </span>
            <div className={styles.footerProgress}>
              <span className={styles.footerPage}>
                Page {currentPage + 1} of {totalPages}
              </span>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{
                    width: `${
                      ((currentChapterIndex * totalPages + currentPage + 1) /
                        (chapters.length * totalPages)) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile navigation */}
        <div className={styles.mobileNavigation}>
          <button
            className={`${styles.mobileNavButton} ${
              currentPage === 0 && currentChapterIndex === 0
                ? styles.disabled
                : ""
            }`}
            onClick={prevPage}
            disabled={currentPage === 0 && currentChapterIndex === 0}
          >
            <ChevronLeft size={18} /> Previous
          </button>
          <button className={styles.mobileMenuButton} onClick={toggleLibrary}>
            <BookOpen size={18} /> Library
          </button>
          <button
            className={`${styles.mobileNavButton} ${
              currentPage === totalPages - 1 &&
              currentChapterIndex === chapters.length - 1
                ? styles.disabled
                : ""
            }`}
            onClick={nextPage}
            disabled={
              currentPage === totalPages - 1 &&
              currentChapterIndex === chapters.length - 1
            }
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookPreview;
